import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ChevronUp, ChevronDown, ChevronsUpDown, Download, Search } from 'lucide-react'
import './ReportTable.css'

/**
 * ReportTable Component
 *
 * A specialized table for displaying report data with sorting, filtering,
 * and export capabilities.
 *
 * @param {Object} props
 * @param {Array} props.columns - Column definitions
 * @param {Array} props.data - Data rows
 * @param {string} props.title - Optional table title
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.searchable - Enable search filtering
 * @param {boolean} props.exportable - Show export buttons
 * @param {Function} props.onExport - Export handler (format) => void
 * @param {string} props.emptyMessage - Message when no data
 * @param {boolean} props.striped - Striped rows
 * @param {boolean} props.compact - Compact mode
 * @param {number} props.maxRows - Maximum rows to display (0 = all)
 */
const ReportTable = ({
	columns = [],
	data = [],
	title,
	loading = false,
	searchable = false,
	exportable = false,
	onExport,
	emptyMessage = 'No data available',
	striped = true,
	compact = false,
	maxRows = 0,
}) => {
	const [sortConfig, setSortConfig] = useState({ key: null, direction: null })
	const [searchQuery, setSearchQuery] = useState('')

	// Handle column sort
	const handleSort = (columnKey) => {
		const column = columns.find((col) => col.key === columnKey)
		if (!column?.sortable) return

		let direction = 'asc'
		if (sortConfig.key === columnKey) {
			if (sortConfig.direction === 'asc') {
				direction = 'desc'
			} else if (sortConfig.direction === 'desc') {
				direction = null
			}
		}

		setSortConfig({ key: columnKey, direction })
	}

	// Filter and sort data
	const processedData = useMemo(() => {
		let result = [...data]

		// Filter by search query
		if (searchQuery) {
			const query = searchQuery.toLowerCase()
			result = result.filter((row) =>
				columns.some((col) => {
					const value = row[col.key]
					return value && String(value).toLowerCase().includes(query)
				})
			)
		}

		// Sort
		if (sortConfig.key && sortConfig.direction) {
			result.sort((a, b) => {
				const aValue = a[sortConfig.key]
				const bValue = b[sortConfig.key]

				if (aValue == null) return 1
				if (bValue == null) return -1

				if (typeof aValue === 'string') {
					return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue)
				}

				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
			})
		}

		// Limit rows
		if (maxRows > 0) {
			result = result.slice(0, maxRows)
		}

		return result
	}, [data, columns, sortConfig, searchQuery, maxRows])

	// Render sort icon
	const renderSortIcon = (columnKey) => {
		if (sortConfig.key !== columnKey) {
			return <ChevronsUpDown size={14} className="report-table__sort-icon--inactive" />
		}
		return sortConfig.direction === 'asc' ? (
			<ChevronUp size={14} className="report-table__sort-icon--active" />
		) : (
			<ChevronDown size={14} className="report-table__sort-icon--active" />
		)
	}

	// Format cell value
	const formatValue = (value, column) => {
		if (column.render) {
			return column.render(value)
		}

		if (value === null || value === undefined) {
			return '-'
		}

		if (typeof value === 'boolean') {
			return value ? 'Yes' : 'No'
		}

		if (column.format === 'number') {
			return Number(value).toLocaleString()
		}

		if (column.format === 'currency') {
			return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
		}

		if (column.format === 'percent') {
			return `${value}%`
		}

		if (column.format === 'date') {
			return new Date(value).toLocaleDateString()
		}

		return value
	}

	const tableClasses = ['report-table', striped ? 'report-table--striped' : '', compact ? 'report-table--compact' : ''].filter(Boolean).join(' ')

	return (
		<div className="report-table-container">
			{(title || searchable || exportable) && (
				<div className="report-table__toolbar">
					{title && <h4 className="report-table__title">{title}</h4>}

					<div className="report-table__toolbar-actions">
						{searchable && (
							<div className="report-table__search">
								<Search size={16} />
								<input
									type="text"
									placeholder="Search..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="report-table__search-input"
								/>
							</div>
						)}

						{exportable && onExport && (
							<div className="report-table__export-buttons">
								<button className="report-table__export-btn" onClick={() => onExport('csv')}>
									<Download size={14} />
									<span>CSV</span>
								</button>
								<button className="report-table__export-btn" onClick={() => onExport('excel')}>
									<Download size={14} />
									<span>Excel</span>
								</button>
								<button className="report-table__export-btn" onClick={() => onExport('pdf')}>
									<Download size={14} />
									<span>PDF</span>
								</button>
							</div>
						)}
					</div>
				</div>
			)}

			<div className="report-table__wrapper">
				<table className={tableClasses}>
					<thead className="report-table__header">
						<tr>
							{columns.map((column) => (
								<th
									key={column.key}
									className={`report-table__header-cell ${column.sortable ? 'report-table__header-cell--sortable' : ''} ${
										column.align ? `report-table__header-cell--${column.align}` : ''
									}`}
									onClick={() => column.sortable && handleSort(column.key)}
									style={{ width: column.width }}
								>
									<div className="report-table__header-content">
										<span>{column.label}</span>
										{column.sortable && renderSortIcon(column.key)}
									</div>
								</th>
							))}
						</tr>
					</thead>

					<tbody className="report-table__body">
						{loading ? (
							<tr>
								<td colSpan={columns.length} className="report-table__loading">
									<div className="report-table__spinner"></div>
									<span>Loading...</span>
								</td>
							</tr>
						) : processedData.length === 0 ? (
							<tr>
								<td colSpan={columns.length} className="report-table__empty">
									{emptyMessage}
								</td>
							</tr>
						) : (
							processedData.map((row, rowIndex) => (
								<tr key={row.id || rowIndex} className="report-table__row">
									{columns.map((column) => (
										<td
											key={column.key}
											className={`report-table__cell ${column.align ? `report-table__cell--${column.align}` : ''}`}
										>
											{formatValue(row[column.key], column)}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>

					{/* Summary Row */}
					{!loading && processedData.length > 0 && columns.some((col) => col.summary) && (
						<tfoot className="report-table__footer">
							<tr>
								{columns.map((column) => (
									<td
										key={column.key}
										className={`report-table__footer-cell ${column.align ? `report-table__footer-cell--${column.align}` : ''}`}
									>
										{column.summary ? calculateSummary(data, column) : ''}
									</td>
								))}
							</tr>
						</tfoot>
					)}
				</table>
			</div>

			{maxRows > 0 && data.length > maxRows && (
				<div className="report-table__pagination-hint">
					Showing {processedData.length} of {data.length} rows
				</div>
			)}
		</div>
	)
}

// Calculate summary for footer
const calculateSummary = (data, column) => {
	const values = data.map((row) => row[column.key]).filter((v) => typeof v === 'number')

	switch (column.summary) {
		case 'sum':
			return values.reduce((a, b) => a + b, 0).toLocaleString()
		case 'avg':
			return values.length ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : 0
		case 'min':
			return values.length ? Math.min(...values).toLocaleString() : '-'
		case 'max':
			return values.length ? Math.max(...values).toLocaleString() : '-'
		case 'count':
			return data.length.toLocaleString()
		default:
			return column.summary
	}
}

ReportTable.propTypes = {
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			sortable: PropTypes.bool,
			align: PropTypes.oneOf(['left', 'center', 'right']),
			width: PropTypes.string,
			format: PropTypes.oneOf(['number', 'currency', 'percent', 'date']),
			render: PropTypes.func,
			summary: PropTypes.oneOfType([PropTypes.oneOf(['sum', 'avg', 'min', 'max', 'count']), PropTypes.string]),
		})
	).isRequired,
	data: PropTypes.array.isRequired,
	title: PropTypes.string,
	loading: PropTypes.bool,
	searchable: PropTypes.bool,
	exportable: PropTypes.bool,
	onExport: PropTypes.func,
	emptyMessage: PropTypes.string,
	striped: PropTypes.bool,
	compact: PropTypes.bool,
	maxRows: PropTypes.number,
}

export default ReportTable
